import { getAuthUser } from "./lib/effectiveAuthUser";
import * as Sentry from "@sentry/react";
import classnames from "classnames";
import firebase from "firebase/app";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useHistory, useLocation } from "react-router-dom";
import Select, { components } from "react-select";
import useMeasure from "react-use-measure";
import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components";
import Auth from "./Auth";
import { sendAppEvent } from "./Utils";
import BillingToast from "./components/BillingToast";
import { Button } from "./components/Button";
import QuotaDetailToolTip from "./components/Pricing/QuotaDetailToolTip";
import SettingsQuotaBar from "./components/Pricing/SettingsQuotaBar";
import RecentActivityLog from "./components/RecentActivityLog";
import { SecretWebSocketHelper } from "./components/SocketHost/SecretWebSocketHelper";
import Toggle from "./components/Toggle";
import useProfile from "./hooks/useProfile";
import { cn } from "./lib/cn";
import usePopoverClose from "./hooks/usePopoverClose";
import Subscription from "./models/Subscription";
import { createSelectStyles, createSelectTheme, selectFocusStyle } from "./styles/a11y";
import UserData, { FONTS, THEMES } from "./models/UserData";
import { useSudowriteStore } from "./stores/sudowriteStore";
import { useStickyTourStore } from "./stores/stickyTourStore";
import StickyNoteAnchor from "./components/StickyTour/StickyNoteAnchor";
import { AnchorIds, TourSignal } from "./components/StickyTour/TourTypes";
import { formatDateWithTimezone } from "./utils/formatDateWithTimezone";
import {
  DEFAULT_PARAGRAPH_SPACING,
  PARAGRAPH_SPACING_OPTIONS,
  normalizeParagraphSpacing,
} from "../../src/shared/constants.js";

const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 40;

const LINE_HEIGHTS = [1.0, 1.5, 2.0];
const lineHeightIconName = (height) => `line-${String(height).replace(".", "")}`;
const lineHeightLabel = (height) => String(height).replace(/\.0$/, "");

// Multipliers of the editor CSS line-height (see getEditorParagraphSpacing).
const PARAGRAPH_SPACING_LABELS = ["1x", "1.5x", "2x"];
const PARAGRAPH_SPACING_ICONS = ["paragraph-1", "paragraph-15", "paragraph-2"];

const SpacingControls = styled.div`
  #settings-container .settings .editor & {
    display: flex;
    margin-bottom: 30px;
    justify-content: center;
    align-items: center;

    button {
      background: none;
      font: inherit;
      cursor: pointer;
      margin-right: 9px;
      border: 2.5px solid var(--menu-color-active);
      border-radius: 5px;
      text-align: center;
      /* Keep the original 71x35 hit target; only size the icon inside. */
      padding: 0 6px;
      width: 71px;
      height: 35px;
      display: flex;
      align-items: center;
      transition:
        border-color 200ms,
        background-color 200ms;
      justify-content: center;
      gap: 3px;
      color: var(--icon-button-fill);
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
      box-sizing: border-box;

      .spacing-value {
        flex: 0 0 auto;
        min-width: 1.1em;
        text-align: left;
      }

      svg {
        width: 24px;
        height: 24px;
        flex: 0 0 auto;
        display: block;
        overflow: visible;
        shape-rendering: crispEdges;

        path {
          fill: var(--icon-button-fill);
        }

        rect {
          fill: none;
        }
      }

      &:last-child {
        margin-right: 0;
      }

      &.selected {
        border: 2.5px solid var(--primary-cta-hover);
      }

      &:hover {
        border: 2.5px solid var(--primary-cta-hover);
        background-color: var(--menu-color-hover);
      }
    }
  }
`;

// Select related styles:
const groupStyles = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "16px",
  // color:"var(--grey-0)"
};
const themeFunction = createSelectTheme({
  neutral0: "var(--menu-color-fullscreen)",
  primary25: "var(--menu-color-hover-2)",
});
const formatOptionLabel = (data) => (
  <div style={groupStyles}>
    <span
      style={{
        fontFamily: UserData.toFontFamily(data.label),
      }}
    >
      {data.label}
    </span>
  </div>
);
const plainOptionLabel = (data) => <div style={groupStyles}>{data.label}</div>;
const styleObject = createSelectStyles({
  control: (base, state) => ({
    ...base,
    display: "flex",
    borderRadius: "5px",
    ...selectFocusStyle(state),
  }),
});
const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator
      {...props}
      styles={{ paddingTop: 10 }}
    >
      <SVG
        aria-label="Open Settings Menu"
        src="/assets/settings_dropdown_icon.svg"
      />
    </components.DropdownIndicator>
  );
};

function SettingsPanel(props) {
  const user = getAuthUser();
  // Bootstrap and identity switches can temporarily have no customer identity.
  // Keep the panel and its effects unmounted until account data can be loaded;
  // changing customers also discards the previous panel's local state.
  if (!user) return null;
  return <SettingsPanelForUser {...props} user={user} key={user.uid} />;
}

function SettingsPanelForUser(props) {
  let history = useHistory();
  const location = useLocation();
  const openStyleSettings = useSudowriteStore((s) => s.openStyleSettings);

  const [editingName, setEditingName] = useState(false);
  const [displayName, setDisplayName] = useState("");

  const { profile } = useProfile();

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || "No Name");
    }
  }, [profile]);

  const user = props.user;
  const email = user.email;

  const updateDisplayName = async (name) => {
    await Auth.fetchWithIdToken("/api/profiles/me", { method: "PUT", displayName: name });
    await firebase.auth().currentUser.updateProfile({
      displayName: name,
    });
  };

  const [subscription, setSubscription] = useState(null);

  const [, setPhotoRefreshedAt] = useState(null);
  useEffect(() => {
    // Refresh cached auth profile to pick up photo changes made on other devices
    user
      .reload()
      .then(() => setPhotoRefreshedAt(Date.now()))
      .catch(() => {});
  }, []);

  // Prefer the user-set photo (e.g. uploaded from mobile) over the OAuth provider photo
  let photoUrl = user.photoURL || (user.providerData[0] ? user.providerData[0].photoURL : null);
  // The =s215 size suffix only applies to Google-hosted photos
  if (photoUrl && photoUrl.includes("googleusercontent.com")) {
    let idx = photoUrl.indexOf("=");
    if (idx != -1) {
      photoUrl = photoUrl.slice(0, idx);
      photoUrl += "=s215";
    }
  }

  let trialDays;
  let free = false;
  let price;

  if (subscription && !subscription.usesQuota) {
    if (subscription.activeTrial()) {
      let difference = props.subscription.data.trialPeriodEnd.getTime() - new Date().getTime();
      trialDays = Math.ceil(difference / (1000 * 3600 * 24));
    }

    if (subscription.activeSubscription()) {
      free = props.subscription.data.stripePlanAmount == 0;
      price = (props.subscription.data.stripePlanAmount / 100.0).toFixed(2);
    }
  }

  const { containerRef: wrapperRef, handleBlur } = usePopoverClose(props.open, () => props.setOpen(false), {
    onEscapeFocus: () => wrapperRef.current?.querySelector("button")?.focus(),
  });
  const selectMenuOpenRef = useRef(false);

  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  // The panel closes through several paths (gear toggle, click-outside blur,
  // Escape, buttons that navigate away), so signal on the open→closed
  // transition rather than in each handler. The signal is delayed past the
  // originating click: if the next tour step listens for GLOBAL_CLICK, an
  // immediate signal would attach that listener in time for the very click
  // that closed the panel, instantly dismissing the step.
  const wasOpenRef = useRef(props.open);
  useEffect(() => {
    if (wasOpenRef.current && !props.open) {
      const timeout = setTimeout(() => sendFlowSignal(TourSignal.SETTINGS_PANEL_CLOSED), 300);
      wasOpenRef.current = props.open;
      return () => clearTimeout(timeout);
    }
    wasOpenRef.current = props.open;
  }, [props.open, sendFlowSignal]);

  useEffect(() => {
    // When the settings panel is closed (props.open becomes false),
    // reset the 'showRecentActivityLog' state to false.
    if (!props.open) {
      setShowRecentActivityLog(false);
    }
  }, [props.open]); // Dependency array includes only 'props.open'

  const [font, setFont] = useState(props.font);
  const [fontSize, setFontSize] = useState(props.fontSize);
  const [textIndent, setTextIndent] = useState(props.textIndent);
  const [lineHeight, setLineHeight] = useState(props.lineHeight);
  const [paragraphSpacing, setParagraphSpacing] = useState(normalizeParagraphSpacing(props.paragraphSpacing));
  const [showWordCount, setShowWordCount] = useState(props.showWordCount);
  const [theme, setTheme] = useState(props.theme);
  const [showCreditsUsedToast, setShowCreditsUsedToast] = useState(props.showCreditsUsedToast);
  const [showRecentActivityLog, setShowRecentActivityLog] = useState(false);

  const [settingsRef, { height }] = useMeasure({ polyfill: ResizeObserver });

  const showQuotaBar = subscription && subscription.usesQuota && !subscription.lapsed();

  const showBillingButton = subscription && ((subscription.hasPaid && !showRecentActivityLog) || subscription.stripeStatus !== null);

  let accountMessage = null;
  if (subscription && !subscription.hasAccessToFeatures()) {
    if (subscription.activeSubscription() && !subscription.goodStanding()) {
      accountMessage = "There is an issue with your payment method. Please go to Billing to update it.";
    } else if (subscription.lapsed()) {
      accountMessage = "Your membership has expired. Please sign up for a membership in order to use AI functions.";
    }
  }

  // Fetches subscription
  useEffect(() => {
    if (props.open) {
      Subscription.fetchFromUserId(user.uid).then((sub) => {
        if (!sub) {
          Sentry.captureMessage("User exists in client but not in db on settings panel", {
            tags: {
              user: user?.uid,
              userEmail: user?.email,
            },
          });
        }
        setSubscription(sub);
      });
    }
  }, [props.open]);

  useEffect(() => {
    props.onSubmit({
      font: font,
      fontSize: fontSize,
      textIndent: textIndent,
      lineHeight: lineHeight,
      paragraphSpacing: paragraphSpacing,
      showWordCount: showWordCount,
      theme: theme,
      showCreditsUsedToast: showCreditsUsedToast,
    });
  }, [font, fontSize, textIndent, lineHeight, paragraphSpacing, showWordCount, theme, showCreditsUsedToast]);

  // SW-8538 Phase B: push /app/plans directly. The Pricing page handles
  // EU-vs-non-EU routing (and opens the VAT modal for EU + !hasPaid users).
  const handleStartMembership = () => {
    sendAppEvent("Settings Subscribe Clicked", {
      ref: "settings",
    });
    history.push("/app/plans?ref=settings");
  };

  let panel = (
    <motion.div
      className="settings"
      ref={settingsRef}
      transition={{ duration: 0.15 }}
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -30 }}
    >
      <section className="membership">
        {!showRecentActivityLog && (
          <button
            onClick={(e) => {
              e.preventDefault();
              props.setOpen(false);
              history.push("/app/projects/deleted");
            }}
            className="deleted-projects"
          >
            View Deleted Projects
          </button>
        )}

        {!showRecentActivityLog && (
          <button
            className="logout"
            onClick={(e) => {
              e.preventDefault();

              Auth.markLogoutInProgress();
              window.location.href = "/logout";
            }}
          >
            Log Out
          </button>
        )}
        <h3 className="user-name">
          {!editingName && (
            <>
              {displayName ? displayName : "No Name"}{" "}
              <a
                onClick={(e) => {
                  setEditingName(true);
                }}
              >
                Edit
              </a>
            </>
          )}

          {editingName && (
            <>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!displayName || displayName.trim().length === 0) {
                    return;
                  }
                  updateDisplayName(displayName);
                  setEditingName(false);
                }}
              >
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                <button>Save</button>
              </form>
            </>
          )}
        </h3>

        <div className="photo">
          {photoUrl && <img src={photoUrl} />}
          {!photoUrl && <div className="email">{email}</div>}
        </div>

        {subscription && (
          <>
            {accountMessage && <div className="message">{accountMessage}</div>}
            {!subscription.activeSubscription() && (
              <>
                {showQuotaBar && (
                  <SettingsQuotaBar
                    subscription={subscription}
                    trial={true}
                    showRecentActivityLog={showRecentActivityLog}
                    setShowRecentActivityLog={setShowRecentActivityLog}
                  />
                )}
                {!showRecentActivityLog && (
                  <div className={cn("start", ["moonbeam", "sorbet"].includes(theme) ? "light-start-span" : "dark-start-span")}>
                    <span>WOW</span>
                    <span>SUPPORTER</span>
                    <span>
                      GREAT
                      <br />
                      MEMBERSHIP
                    </span>
                    <span>
                      PATRON
                      <br />
                      OF THE ARTS
                    </span>
                    <span>
                      AIs NEED
                      <br />
                      TO EAT TOO
                    </span>
                    {subscription.usesQuota && <button onClick={handleStartMembership}>Start Membership</button>}
                    {!subscription.usesQuota && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          history.push("/app/plans?ref=settings_trial");
                        }}
                      >
                        Start Membership
                      </button>
                    )}
                  </div>
                )}

                {!subscription.usesQuota && subscription.activeTrial() && !showRecentActivityLog && (
                  <p className="explain">
                    ${subscription.priceToOffer()}/month after your free trial expires on {formatDateWithTimezone(subscription.data.trialPeriodEnd)}
                  </p>
                )}
                {subscription.usesQuota && subscription.activeTrial() && !showRecentActivityLog && <p className="explain">Plans start at $10/month</p>}
                {!subscription.usesQuota && !subscription.activeTrial() && !showRecentActivityLog && <p>Your trial membership expired! Start your membership to use Sudowrite.</p>}
              </>
            )}

            {subscription.activeSubscription() && (
              <>
                {!subscription.usesQuota && (
                  <p>
                    Next Billing Cycle Starts On
                    <br />
                    {formatDateWithTimezone(subscription.data.currentPeriodEnd)}
                  </p>
                )}

                {showQuotaBar && (
                  <QuotaDetailToolTip subscription={subscription}>
                    <SettingsQuotaBar
                      subscription={subscription}
                      trial={true}
                      showRecentActivityLog={showRecentActivityLog}
                      setShowRecentActivityLog={setShowRecentActivityLog}
                    />
                  </QuotaDetailToolTip>
                )}

                {!showRecentActivityLog && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      history.push("/app/plans?ref=settings");
                    }}
                    className="plans"
                  >
                    Manage Membership…
                  </button>
                )}
              </>
            )}

            {showBillingButton && (
              <button
                onClick={(e) => {
                  window.location.href = "/app/subscription/portal";
                }}
                className="billing"
              >
                Change Billing Info…
              </button>
            )}

            {location.pathname.startsWith("/app/project") && (
              <button
                onClick={() => {
                  openStyleSettings();
                  if (props.setOpen) props.setOpen(false);
                }}
                className="billing"
              >
                More Settings…
              </button>
            )}


            {subscription.usesLedgers && showRecentActivityLog && (
              <RecentActivityLog
                subscription={subscription}
                onClose={() => setShowRecentActivityLog(false)}
                showCreditsUsedToast={showCreditsUsedToast}
                setShowCreditsUsedToast={setShowCreditsUsedToast}
                parentHeight={height}
              ></RecentActivityLog>
            )}
          </>
        )}
      </section>

      <section className="editor">
        <h3 className="font-label">Font</h3>
        <div className="fonts">
          <Select
            aria-label="Select Font"
            formatOptionLabel={formatOptionLabel}
            styles={styleObject}
            theme={themeFunction}
            components={{ DropdownIndicator }}
            className={"select"}
            value={{ value: font, label: font }}
            isSearchable={false}
            onChange={(selectedOption) => {
              setFont(selectedOption.value);
            }}
            onMenuOpen={() => { selectMenuOpenRef.current = true; }}
            onMenuClose={() => { selectMenuOpenRef.current = false; }}
            onKeyDown={(e) => {
              if (e.key === "Escape" && selectMenuOpenRef.current) {
                e.stopPropagation();
              }
            }}
            options={FONTS}
          />
        </div>

        <h3>Text Size</h3>

        <div className="font-size">
          <div className="small">A</div>
          <div className="slider">
            <Slider
              handleStyle={{ width: 14.66, height: 14.66, marginTop: -4, borderColor: "transparent", boxShadow: "var(--slider-handle-drop-shadow)", opacity: 1 }}
              min={MIN_FONT_SIZE}
              max={MAX_FONT_SIZE}
              railStyle={{ backgroundColor: "var(--slider-rail)", height: 7.8 }}
              trackStyle={{ backgroundColor: "var(--slider-track)", height: 7.8 }}
              value={fontSize}
              onChange={(value) => {
                setFontSize(value);
              }}
              style={{ zIndex: 0 }}
              ariaLabelForHandle="Select Text Size"
              ariaValueTextFormatterForHandle={(value) => `${value} points`}
            />
          </div>
          <div className="large">A</div>
        </div>

        <h3>Line Spacing</h3>

        <SpacingControls>
          {LINE_HEIGHTS.map((height, i) => {
            const label = lineHeightLabel(height);
            return (
              <button
                type="button"
                key={i}
                className={classnames({ selected: height == lineHeight })}
                onClick={() => {
                  setLineHeight(height);
                }}
                aria-label={`Line spacing ${label}`}
                aria-pressed={height == lineHeight}
              >
                <span className="spacing-value">{label}</span>
                <SVG src={`/assets/${lineHeightIconName(height)}.svg`} />
              </button>
            );
          })}
        </SpacingControls>

        <h3 style={{ position: "relative" }}>
          Paragraph Spacing
          <StickyNoteAnchor anchorId={AnchorIds.SETTINGS_PARAGRAPH_SPACING} />
        </h3>

        <SpacingControls className="paragraph-spacing">
          {PARAGRAPH_SPACING_OPTIONS.map((spacing, i) => {
            const label = PARAGRAPH_SPACING_LABELS[i];
            const selected = spacing === normalizeParagraphSpacing(paragraphSpacing);
            return (
              <button
                type="button"
                key={i}
                className={classnames({ selected })}
                onClick={() => {
                  setParagraphSpacing(spacing);
                }}
                aria-label={`Paragraph spacing ${label}`}
                aria-pressed={selected}
                title={label}
              >
                <span className="spacing-value">{label}</span>
                <SVG src={`/assets/${PARAGRAPH_SPACING_ICONS[i]}.svg`} />
              </button>
            );
          })}
        </SpacingControls>

        <div
          className="divider"
          // TODO: Put in stylesheet or JS style component
          style={{ borderBottom: "1px solid var(--divider)", width: "calc(100% + 50px)", position: "relative", left: -25, marginBottom: 30 }}
        ></div>

        <div className="theme">
          <h3>Theme</h3>
          <Select
            aria-label="Select Theme"
            formatOptionLabel={plainOptionLabel}
            styles={{
              ...styleObject,
            }}
            theme={themeFunction}
            components={{ DropdownIndicator }}
            className={"select"}
            value={THEMES.find((t) => t.value === theme)}
            isSearchable={false}
            onChange={(selectedOption) => {
              setTheme(selectedOption.value);
            }}
            onMenuOpen={() => { selectMenuOpenRef.current = true; }}
            onMenuClose={() => { selectMenuOpenRef.current = false; }}
            onKeyDown={(e) => {
              if (e.key === "Escape" && selectMenuOpenRef.current) {
                e.stopPropagation();
              }
            }}
            options={THEMES}
          />
        </div>

        <div className="toggle-setting">
          <Toggle set={setShowWordCount} checked={showWordCount} id="wordCount" role="switch" />
          <label htmlFor="wordCount">Show word count</label>
        </div>
        <div className="toggle-setting">
          <Toggle set={setTextIndent} checked={textIndent} id="indent" role="switch" />
          <label htmlFor="indent">Indent paragraphs</label>
        </div>
        <div className="affiliate-plug">
          <h2>🗣️</h2>
          <p>
            Join{" "}
            <a
              href="https://sudowrite.notion.site/Sudowrite-Affiliate-Program-12788b26b71746f2a72cd05b7087e8c9"
              target="_blank"
            >
              Sudowrite Affiliates
            </a>{" "}
            to earn money helping people discover Sudowrite!
          </p>
        </div>
        {/* <div className="policies">
          <a href="https://sudowrite.notion.site/Privacy-Policy-710e53e814f8491b8ee2634ee9162ed8">Privacy Policy</a>
          &nbsp; &nbsp; &nbsp;
          <a href="https://sudowrite.notion.site/Terms-and-Conditions-83ddd78e1ab04d9d88321a9ef7665925">Terms of Use</a>
        </div> */}

        <a
          href="#"
          className="reset"
          onClick={(e) => {
            e.preventDefault();
            let values = UserData.defaultValues();
            setFont(values.font);
            setFontSize(values.fontSize);
            setTextIndent(values.textIndent);
            setLineHeight(values.lineHeight);
            setParagraphSpacing(DEFAULT_PARAGRAPH_SPACING);
            setShowWordCount(values.showWordCount);
          }}
        >
          Reset to default
        </a>
      </section>
    </motion.div>
  );

  return (
    <>
      <div
        id="settings-container"
        className={classnames({ "settings-open": props.open })}
        ref={wrapperRef}
        onBlur={handleBlur}
      >
        <SecretWebSocketHelper />
        <BillingToast showCreditsUsedToast={showCreditsUsedToast} />
        <StickyNoteAnchor anchorId={AnchorIds.SETTINGS_GEAR} />
        <Button
          secondaryGrey={true}
          className="use-component-styles settings-button"
          aria-label="Open Settings Menu"
          iconSize={24}
          onClick={() => {
            if (!props.open) {
              sendFlowSignal(TourSignal.SETTINGS_GEAR_CLICKED);
            }
            props.setOpen(!props.open);
          }}
        >
          <SVG
            className="gear"
            src={props.v2 ? "/assets/gear-v2.svg" : "/assets/gear.svg"}
          />
        </Button>
        <AnimatePresence>
          {props.open && panel}
        </AnimatePresence>
      </div>
    </>
  );
}

export default SettingsPanel;
